/**
 * Typed endpoint wrappers. Each function corresponds to one backend route.
 * Centralizing them keeps the page code free of inline `/api/...` strings
 * and makes refactors easy.
 */

import { api } from './client'

// ─── Auth ───────────────────────────────────────────────────────
export const auth = {
  sendOtp: (phone: string, purpose: 'login' | 'order_verification' = 'login') =>
    api<{ ok: boolean; message: string; expiresIn: number; devOtp?: string }>('/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ phone, purpose }),
    }),
  verifyOtp: (phone: string, otp: string, purpose: 'login' | 'order_verification' = 'login') =>
    api<{ user: AdminUser; token: string; isNewUser: boolean }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phone, otp, purpose }),
    }),
  me: () => api<{ user: AdminUser }>('/auth/me'),
  logout: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
}

// ─── Shared types ───────────────────────────────────────────────
export interface AdminUser {
  id: number
  name: string
  email: string | null
  phone: string
  role: 'customer' | 'admin' | 'author'
  avatar: string | null
  /** Author-profile fields. Populated for users with role='author'
   *  or any user who has authored at least one post. */
  authorSlug: string | null
  bio: string | null
  authorAvatar: string | null
}

// ─── Dashboard ──────────────────────────────────────────────────
export interface DashboardStats {
  todayOrders: number
  todayRevenue: number
  mtdRevenue: number
  totalCustomers: number
  totalOrders: number
  pendingContact: number
  lowStockCount: number
  recentOrders: RecentOrder[]
}

export interface RecentOrder {
  id: string
  orderNumber: string
  status: 'created' | 'paid' | 'cancelled' | 'delivered'
  amount: number
  customerName: string
  customerPhone: string
  itemCount: number
  createdAt: string
}

export const admin = {
  stats: () => api<DashboardStats>('/admin/stats'),
}

// ─── Orders ─────────────────────────────────────────────────────
export type OrderStatus = 'created' | 'paid' | 'cancelled' | 'delivered' | 'returned'

export interface OrderCustomer {
  id: number
  name: string
  phone: string
  email: string | null
}

export interface AdminOrder {
  id: string
  orderNumber: string
  status: OrderStatus
  amount: number
  subtotal: number
  shipping: number
  gstAmount: number
  customer: OrderCustomer
  itemCount: number
  createdAt: string
  paidAt: string | null
  cancelledAt: string | null
  createdByAdmin: boolean
}

export interface AdminOrderDetail extends AdminOrder {
  /** Optional internal note the admin can attach to an order. */
  adminNote: string | null
  /** Set when status flips paid → delivered via manifest. */
  deliveredAt: string | null
  /** Signed paise amount (positive = surcharge, negative = credit)
   *  applied on top of subtotal+shipping+gst to compute the final
   *  `amount`. Persisted on the server so the adjustment survives
   *  subsequent re-edits. */
  adjustmentAmount: number | null
  /** Optional human-readable reason for the cash adjustment. */
  adjustmentNote: string | null
  items: Array<{
    id: number
    name: string
    price: number
    quantity: number
    imageUrl: string | null
    gstRate: number | null
    gstAmount: number | null
  }>
  address: Record<string, unknown>
  devMode?: boolean
}

export const orders = {
  list: (params: {
    status?: OrderStatus
    search?: string
    page?: number
    limit?: number
  } = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') qs.append(k, String(v))
    })
    return api<{ orders: AdminOrder[]; total: number; page: number; limit: number }>(
      `/admin/orders${qs.toString() ? `?${qs}` : ''}`,
    )
  },
  updateStatus: (id: string, status: OrderStatus) =>
    api<{ order: AdminOrder }>(`/admin/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  detail: (id: string) => api<{ order: AdminOrderDetail }>(`/admin/orders/${id}`),
  /** Add a line item to an existing order. Server resolves product
   *  price/name/image and runs allocateFifo if the order has already
   *  consumed inventory. */
  addItem: (id: string, productId: string, quantity: number) =>
    api<{ order: AdminOrderDetail }>(`/admin/orders/${id}/items`, {
      method: 'POST',
      body: JSON.stringify({ productId, quantity }),
    }),
  /** Update address and/or adminNote. Both fields optional. */
  updateMeta: (
    id: string,
    data: { address?: Record<string, unknown>; adminNote?: string | null },
  ) =>
    api<{ order: AdminOrderDetail }>(`/admin/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /** Manifest a paid order as delivered. Idempotent on already-
   *  delivered (no-op). Rejects cancelled/returned/created. */
  manifest: (id: string) =>
    api<{ order: AdminOrderDetail }>(`/admin/orders/${id}/manifest`, { method: 'POST' }),
  /** Set the cash adjustment on an existing order. Signed paise —
   *  positive for surcharges, negative for credits. Server recomputes
   *  `amount` after the patch. */
  setAdjustment: (
    id: string,
    data: {
      adjustmentAmount: number | null
      adjustmentNote?: string | null
    },
  ) =>
    api<{ order: AdminOrderDetail }>(`/admin/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /** Create an order as admin. Status can be set explicitly
   *  ('created' / 'paid' / 'delivered'); allocation runs immediately
   *  for paid and delivered orders. Customer is resolved by id, or
   *  inline-created by phone if not in the DB. */
  adminCreate: (data: {
    customer: {
      id?: number
      phone?: string
      name?: string
      email?: string | null
    }
    items: Array<{ productId: string; quantity: number }>
    shippingAddress: Record<string, unknown>
    status?: 'created' | 'paid' | 'delivered'
    adminNote?: string
  }) =>
    api<{ order: AdminOrderDetail }>(`/admin/orders`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** Partial or full return on a delivered order. Stock is restored
   *  to the original batches via OrderBatchAllocation; status flips
   *  to 'returned' only when every line item is fully returned. */
  return: (
    id: string,
    data: {
      items: Array<{ orderItemId: number; quantity: number; reason?: string }>
      adminNote?: string
    },
  ) =>
    api<{ order: AdminOrder; return: ReturnRecord }>(`/admin/orders/${id}/return`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

/** Audit row for an order return event. */
export interface ReturnRecord {
  id: number
  orderId: string
  items: Array<{ orderItemId: number; quantity: number; reason?: string }>
  adminNote: string | null
  processedById: number | null
  createdAt: string
}

// ─── Inventory ──────────────────────────────────────────────────
export interface ProductBatch {
  id: number
  productId: string
  batchNumber: string
  purchasePrice: number
  quantityPerUnit: number
  unitType: string
  remainingQuantity: number
  supplierName: string | null
  purchaseDate: string
  expiryDate: string | null
}

/** Payload accepted by PATCH /api/inventory/batches/:id. All fields
 *  optional — server applies only what's sent. */
export interface UpdateBatchPayload {
  batchNumber?: string
  supplierName?: string | null
  purchaseDate?: string
  expiryDate?: string | null
  unitType?: string
  purchasePrice?: number
  quantityPerUnit?: number
  remainingQuantity?: number
}

export interface LowStockItem {
  productId: string
  name: string
  slug: string
  totalStock: number
  threshold: number
}

export const inventory = {
  batches: (productId?: string) =>
    api<{ batches: ProductBatch[] }>(
      `/inventory/batches${productId ? `?productId=${productId}` : ''}`,
    ),
  lowStock: (threshold = 5) =>
    api<{ items: LowStockItem[] }>(`/inventory/low-stock?threshold=${threshold}`),
  createBatch: (data: {
    productId: string
    batchNumber: string
    purchasePrice: number
    quantityPerUnit: number
    unitType?: string
    supplierName?: string
    purchaseDate: string
    expiryDate?: string
  }) =>
    api<{ batch: ProductBatch }>(`/inventory/batches`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateBatch: (id: number, data: UpdateBatchPayload) =>
    api<{ batch: ProductBatch }>(`/inventory/batches/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteBatch: (id: number) =>
    api<{ ok: true }>(`/inventory/batches/${id}`, { method: 'DELETE' }),
}

// ─── Users ──────────────────────────────────────────────────────
export interface AdminUserListItem {
  id: number
  name: string
  email: string | null
  phone: string
  role: 'customer' | 'admin' | 'author'
  avatarUrl: string | null
  authorSlug: string | null
  bio: string | null
  authorAvatar: string | null
  isActive: boolean
  createdAt: string
  orderCount: number
  wishlistCount: number
}

export interface AdminUserDetail extends AdminUserListItem {
  counts: { orders: number; wishlistItems: number; addresses: number }
  totalSpent: number
  recentOrders: Array<{
    id: string
    orderNumber: string
    amount: number
    status: OrderStatus
    createdAt: string
  }>
}

export const users = {
  list: (params: {
    search?: string
    role?: 'customer' | 'admin' | 'author'
    isActive?: boolean
    page?: number
    limit?: number
  } = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') qs.append(k, String(v))
    })
    return api<{ users: AdminUserListItem[]; total: number; page: number; limit: number }>(
      `/admin/users${qs.toString() ? `?${qs}` : ''}`,
    )
  },
  detail: (id: number) => api<{ user: AdminUserDetail }>(`/admin/users/${id}`),
  update: (id: number, data: Partial<{ name: string; email: string | null; role: 'customer' | 'admin' | 'author'; isActive: boolean }>) =>
    api<{ user: AdminUserListItem }>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
}

// ─── Contact ────────────────────────────────────────────────────
export interface ContactSubmission {
  id: number
  name: string
  email: string
  subject: string
  message: string
  isRead: boolean
  createdAt: string
}

export const contact = {
  list: (params: { isRead?: boolean; page?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v != null) qs.append(k, String(v))
    })
    return api<{ submissions: ContactSubmission[]; total: number; page: number; limit: number }>(
      `/admin/contact${qs.toString() ? `?${qs}` : ''}`,
    )
  },
  markRead: (id: number) =>
    api<{ submission: ContactSubmission }>(`/admin/contact/${id}/read`, { method: 'PATCH' }),
}

// ─── Products ──────────────────────────────────────────────────
export interface AdminProduct {
  id: string
  slug: string
  name: string
  category: { id: string; slug: string; name: string }
  price: number
  originalPrice: number
  discount: number
  gstRate: number
  image: string | null
  infoImage: string | null
  description: string
  howToUse: string
  size: string | null
  featured: boolean
  trending: boolean
  bestseller: boolean
  new: boolean
  isActive: boolean
  /** SEO metadata. Server falls back to name + description when these
   *  are null. Stored as opaque strings — admin UI surfaces them. */
  metaTitle: string | null
  metaDescription: string | null
  /** Additional product photos (in addition to `image`). Max 4 entries
   *  (cover counts as 1 — total cap of 5 per product). URL is absolute
   *  (server converts via PUBLIC_BASE_URL). */
  gallery: Array<{ id: number; url: string | null; sortOrder: number }>
  benefits: string[]
  ingredients: string[]
  reviewCount: number
  orderCount: number
  /** Live total stock (sum of remainingQuantity across all batches).
   *  Surfaced in the admin order-create picker so the admin sees
   *  how many units are actually available before picking a
   *  quantity. */
  totalStock: number
  createdAt: string
}

export interface AdminProductDetail extends AdminProduct {
  updatedAt: string
}

/** A single hero carousel slide. The admin UI sends/receives
 *  these as FormData with two image files (`heroImageMobile` and
 *  `heroImageDesktop`); the strings here are the resulting absolute
 *  URLs. */
export interface HeroSlide {
  id: number
  imageMobileUrl: string
  imageDesktopUrl: string
  linkUrl: string
  sortOrder: number
  isActive: boolean
}

export const products = {
  list: (params: {
    category?: string
    search?: string
    isActive?: string
    page?: number
    limit?: number
  } = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') qs.append(k, String(v))
    })
    return api<{ products: AdminProduct[]; total: number; page: number; limit: number }>(
      `/admin/products${qs.toString() ? `?${qs}` : ''}`,
    )
  },
  detail: (slug: string) =>
    api<{ product: AdminProductDetail }>(`/admin/products/${slug}`),
  /* `data` may be a plain object (JSON) or a FormData instance when
     the editor is uploading a freshly-picked image. The api() client
     handles both cases — it sets Content-Type only for JSON. */
  create: (data: Record<string, unknown> | FormData) =>
    api<{ product: AdminProduct }>('/admin/products', {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  update: (slug: string, data: Record<string, unknown> | FormData) =>
    api<{ product: AdminProduct }>(`/admin/products/${slug}`, {
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  delete: (slug: string) =>
    api<{ ok: true }>(`/admin/products/${slug}`, { method: 'DELETE' }),
}

// ─── Product categories ─────────────────────────────────────────
export interface AdminCategory {
  id: string
  slug: string
  name: string
  sortOrder: number
  productCount: number
  createdAt?: string
}

export const categories = {
  list: () => api<{ categories: AdminCategory[] }>('/admin/categories'),
  create: (data: {
    slug: string
    name: string
    sortOrder?: number
  }) =>
    api<{ category: AdminCategory }>('/admin/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (
    id: string,
    data: Partial<{
      slug: string
      name: string
      sortOrder: number
    }>,
  ) =>
    api<{ category: AdminCategory }>(`/admin/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    api<{ ok: true }>(`/admin/categories/${id}`, { method: 'DELETE' }),
}

// ─── Blog ───────────────────────────────────────────────────────
export interface BlogPost {
  id: number
  title: string
  slug: string
  excerpt: string
  content: string
  coverImage: string | null
  categoryId: number | null
  category?: { id: number; name: string; slug: string } | null
  /** Linked User.id. The server snapshots name + avatar onto the post
   *  on create/update, so the public API doesn't need to resolve this. */
  authorUserId: number | null
  authorName: string
  authorAvatar: string | null
  tags: string[]
  isPublished: boolean
  publishedAt: string | null
  metaTitle: string | null
  metaDescription: string | null
  createdAt: string
  updatedAt: string
}

export interface BlogCategory {
  id: number
  name: string
  slug: string
  description: string | null
  postCount?: number
}

export interface AuthorSummary {
  id: number
  name: string
  phone: string
  role: string
  avatarUrl: string | null
  authorSlug: string | null
  authorAvatar: string | null
  bio: string | null
  postCount: number
}

export const hero = {
  /** Public — used by the storefront SPA. Returns active slides only. */
  publicList: () => api<{ slides: HeroSlide[] }>('/hero'),
  /** Admin — list all slides (including inactive) for the admin UI. */
  list: () => api<{ slides: HeroSlide[] }>('/admin/hero'),
  /** Admin — create a slide. Caller must build a FormData with
   *  `heroImageMobile` (file) + `heroImageDesktop` (file) +
   *  `linkUrl` + `sortOrder` + `isActive` fields. */
  create: (form: FormData) =>
    api<{ slide: HeroSlide }>('/admin/hero', { method: 'POST', body: form }),
  /** Admin — partial update. Same FormData shape as create; omitted
   *  image fields leave the existing files untouched. */
  update: (id: number, form: FormData) =>
    api<{ slide: HeroSlide }>(`/admin/hero/${id}`, {
      method: 'PATCH',
      body: form,
    }),
  /** Admin — hard delete. */
  delete: (id: number) =>
    api<{ ok: true }>(`/admin/hero/${id}`, { method: 'DELETE' }),
}

export const blog = {
  // Public list (already exists)
  listAll: () => api<{ posts: BlogPost[] }>('/blog/posts?limit=100'),
  listCategories: () => api<{ categories: BlogCategory[] }>('/blog/categories'),
  detail: (id: number) => api<{ post: BlogPost }>(`/admin/blog/posts/${id}`),
  // Admin-only CRUD
  /* `data` may be a plain object (JSON) or a FormData instance when
     the editor is uploading a cover image. The api() client handles
     both cases — it sets Content-Type only for JSON. */
  create: (data: Partial<BlogPost> | FormData) =>
    api<{ post: BlogPost }>('/blog/posts', {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  update: (id: number, data: Partial<BlogPost> | FormData) =>
    api<{ post: BlogPost }>(`/blog/posts/${id}`, {
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  delete: (id: number) => api<{ ok: true }>(`/blog/posts/${id}`, { method: 'DELETE' }),
  togglePublish: (id: number) =>
    api<{ post: BlogPost }>(`/blog/posts/${id}/publish`, { method: 'PUT' }),

  // Categories
  createCategory: (data: { name: string; slug?: string; description?: string | null }) =>
    api<{ category: BlogCategory }>('/blog/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateCategory: (id: number, data: Partial<{ name: string; slug: string; description: string | null }>) =>
    api<{ category: BlogCategory }>(`/blog/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteCategory: (id: number) =>
    api<{ ok: true }>(`/blog/categories/${id}`, { method: 'DELETE' }),
}

// ─── Authors ────────────────────────────────────────────────────
export const authors = {
  list: () => api<{ authors: AuthorSummary[] }>('/admin/authors'),
  /* Update the author-specific profile fields on a user. Pass a
     FormData when also uploading a new authorAvatar; otherwise JSON. */
  updateProfile: (
    userId: number,
    data:
      | { bio?: string | null; authorSlug?: string | null; authorAvatar?: string | null }
      | FormData,
  ) =>
    api<{ user: AdminUser }>(`/admin/users/${userId}/author-profile`, {
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
}

// ─── Affiliates ─────────────────────────────────────────────────
export interface Affiliate {
  id: number
  userId: number
  name: string | null
  email: string | null
  phone: string | null
  referralCode: string
  commissionRate: number
  status: 'active' | 'suspended' | 'banned'
  bankDetails: BankDetails | null
  totalEarned: number
  totalPaid: number
  pendingPayout: number
  createdAt: string
}

export interface AffiliateListItem extends Affiliate {
  userActive: boolean
  referralsCount: number
  ledgerCount: number
  payoutsCount: number
}

export interface BankDetails {
  accountName: string
  accountNumber: string
  ifsc: string
  bankName: string
}

export interface LedgerEntry {
  id: number
  orderId: string
  orderNumber: string
  amount: number
  type: 'earned' | 'reversed' | 'adjustment'
  note: string | null
  createdAt: string
}

export interface Payout {
  id: number
  amount: number
  reference: string | null
  status: 'pending' | 'completed' | 'failed'
  note: string | null
  createdAt: string
}

export const affiliates = {
  list: (params: {
    status?: 'active' | 'suspended' | 'banned'
    search?: string
    page?: number
    limit?: number
  } = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') qs.append(k, String(v))
    })
    return api<{
      affiliates: AffiliateListItem[]
      total: number
      page: number
      limit: number
    }>(`/admin/affiliates${qs.toString() ? `?${qs}` : ''}`)
  },
  detail: (id: number) => api<{ affiliate: Affiliate }>(`/admin/affiliates/${id}`),
  ledger: (id: number) =>
    api<{ ledger: LedgerEntry[]; payouts: Payout[] }>(`/admin/affiliates/${id}/ledger`),
  create: (data: {
    phone: string
    name: string
    email?: string | null
    referralCode?: string
    commissionRate?: number
    bankDetails?: BankDetails | null
  }) =>
    api<{ affiliate: Affiliate }>('/admin/affiliates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (
    id: number,
    data: Partial<{
      name: string
      email: string | null
      commissionRate: number
      status: 'active' | 'suspended' | 'banned'
      bankDetails: BankDetails | null
    }>,
  ) =>
    api<{ affiliate: Affiliate }>(`/admin/affiliates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  recordPayout: (id: number, data: { amount: number; reference?: string; note?: string }) =>
    api<{ payout: Payout }>(`/admin/affiliates/${id}/payout`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

// ─── Settings ──────────────────────────────────────────────────
export const settings = {
  get: (group?: string) =>
    api<{ settings: Record<string, string> }>(`/admin/settings${group ? `?group=${group}` : ''}`),
  update: (data: Record<string, string>) =>
    api<{ settings: Record<string, string> }>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
}

// ─── Reviews (admin) ───────────────────────────────────────────
export interface ProductReview {
  id: string
  productId: string
  productName?: string
  productSlug?: string
  author: string
  location: string | null
  rating: number
  title: string
  body: string
  date: string
  verified: boolean
  helpful: number
  avatar: string | null
}

export const reviews = {
  list: (params: { productId?: string; page?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') qs.append(k, String(v))
    })
    return api<{ reviews: ProductReview[]; total: number; page: number; limit: number }>(
      `/admin/reviews${qs.toString() ? `?${qs}` : ''}`,
    )
  },
  approve: (id: string) =>
    api<{ review: ProductReview }>(`/admin/reviews/${id}/approve`, { method: 'PATCH' }),
  update: (id: string, data: { rating?: number; title?: string; body?: string }) =>
    api<{ review: ProductReview }>(`/admin/reviews/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    api<{ ok: true }>(`/admin/reviews/${id}`, { method: 'DELETE' }),
  import: (entries: Array<{ productId: string; author: string; location?: string; rating: number; title: string; body: string; date?: string }>) =>
    api<{ count: number }>('/admin/reviews/import', { method: 'POST', body: JSON.stringify({ entries }) }),
  /* Admin-authored single-row create from the Add Review dialog.
   * productId accepts slug or UUID; the server's lookupOrThrow
   * resolves to the real product id before persisting. By default
   * the row is verified — admin can flip verified: false if they
   * want to hold the row for a manual approval pass. */
  create: (data: { productId: string; author: string; location?: string; rating: number; title: string; body: string; verified?: boolean }) =>
    api<{ review: ProductReview }>('/admin/reviews', { method: 'POST', body: JSON.stringify(data) }),
}

// ─── Customer Testimonials (admin) ─────────────────────────────
/* Shape returned by the testimonials list/CRUD endpoints. The
 * `productId` field is optional — pre-existing rows were written
 * before the picker existed and only have `product`. New rows can
 * carry both. */
export interface CustomerTestimonial {
  id: string
  name: string
  location: string | null
  rating: number
  text: string
  image: string | null
  videoUrl: string | null
  product: string
  /** Optional for legacy rows — new rows written via the picker
   *  carry it so the editor can round-trip the selection. */
  productId: string | null
  date: string
  verified: boolean
}

export const testimonials = {
  list: () => api<{ testimonials: CustomerTestimonial[] }>('/admin/testimonials'),
  /* Accepts a plain object (JSON) when the dialog is opened without
   * changing the photo, or FormData with a `testimonialImage` Blob
   * when a fresh photo is being uploaded. The api() client detects
   * FormData and sets multipart headers automatically. */
  create: (data: Record<string, unknown> | FormData) =>
    api<{ testimonial: CustomerTestimonial }>('/admin/testimonials', {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  update: (id: string, data: Record<string, unknown> | FormData) =>
    api<{ testimonial: CustomerTestimonial }>(`/admin/testimonials/${id}`, {
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  delete: (id: string) =>
    api<{ ok: true }>(`/admin/testimonials/${id}`, { method: 'DELETE' }),
  import: (entries: Array<{ name: string; location?: string; rating: number; text: string; productName: string; imageUrl?: string; videoUrl?: string; verified?: boolean; date?: string }>) =>
    api<{ count: number }>('/admin/testimonials/import', { method: 'POST', body: JSON.stringify({ entries }) }),
  /* Lightweight picker — used in the dialog to populate the
   * <select>. Returns active products only (server-side filter)
   * with just the fields the picker needs to render. */
  picker: () =>
    api<{ products: Array<{ id: string; name: string; slug: string; categoryName: string | null }> }>(
      '/admin/products/picker',
    ),
}

// ─── Video Testimonials (admin) ────────────────────────────────
export interface AdminVideoTestimonial {
  id: string
  productId: string
  productName?: string
  productSlug?: string
  author: string
  location: string | null
  thumbnail: string
  videoUrl: string
  duration: string
  date: string
}

export const videoTestimonials = {
  list: (params: { productId?: string } = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') qs.append(k, String(v))
    })
    return api<{ videos: AdminVideoTestimonial[] }>(
      `/admin/video-testimonials${qs.toString() ? `?${qs}` : ''}`,
    )
  },
  create: (data: { productId: string; author: string; location?: string; thumbnailUrl: string; videoUrl: string; duration: string }) =>
    api<{ video: AdminVideoTestimonial }>('/admin/video-testimonials', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<AdminVideoTestimonial>) =>
    api<{ video: AdminVideoTestimonial }>(`/admin/video-testimonials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    api<{ ok: true }>(`/admin/video-testimonials/${id}`, { method: 'DELETE' }),
}

// ─── FAQ (admin) ───────────────────────────────────────────────
/* The /admin/faq endpoint returns the full tree in one fetch —
 *  categories with items nested. The /categories and /items sub-
 *  routes exist for places that need a flat list (e.g. an item
 *  picker). The shapes are intentionally simple: question/answer
 *  are plain text mirrors of the public /content/faq shape. */
export interface AdminFaqItem {
  id: number
  categoryId: number
  question: string
  answer: string
  sortOrder: number
}

export interface AdminFaqCategory {
  id: number
  name: string
  sortOrder: number
  itemCount: number
  createdAt?: string
  /** Populated only by the /admin/faq tree endpoint, not the flat
   *  /categories list. */
  items?: AdminFaqItem[]
}

export const faq = {
  /** Tree — categories with items nested. Single round-trip for the
   *  page render. */
  list: () => api<{ categories: AdminFaqCategory[] }>('/admin/faq'),
  /** Flat category list (no items). */
  listCategories: () =>
    api<{ categories: AdminFaqCategory[] }>('/admin/faq/categories'),
  createCategory: (data: { name: string; sortOrder?: number }) =>
    api<{ category: AdminFaqCategory }>('/admin/faq/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateCategory: (id: number, data: { name?: string; sortOrder?: number }) =>
    api<{ category: AdminFaqCategory }>(`/admin/faq/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteCategory: (id: number) =>
    api<{ ok: true }>(`/admin/faq/categories/${id}`, { method: 'DELETE' }),
  /** Flat item list (across all categories). */
  listItems: () => api<{ items: AdminFaqItem[] }>('/admin/faq/items'),
  createItem: (data: { categoryId: number; question: string; answer: string; sortOrder?: number }) =>
    api<{ item: AdminFaqItem }>('/admin/faq/items', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateItem: (id: number, data: Partial<{ categoryId: number; question: string; answer: string; sortOrder: number }>) =>
    api<{ item: AdminFaqItem }>(`/admin/faq/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteItem: (id: number) =>
    api<{ ok: true }>(`/admin/faq/items/${id}`, { method: 'DELETE' }),
}
